import type { WorkQueueAction, WorkQueueJob } from "../../../lib/corpusApi";
import { ROUTES } from "../../../route_paths";
import { BATCH_JOB_KIND_VIEWS } from "../BatchJobs/batch_job_kinds";

/** One batch job inside an action card: what it is, what it owes, and where to deal with it. */
export interface WorkQueueJobView {
  id: string;
  name: string;
  kindLabel: string;
  status: WorkQueueJob["status"];
  /** What the job owes ("3 open reviews"); null when the count says nothing (a failed job with
   *  no failed items). */
  owes: string | null;
  to: string;
}

/** A work-queue action as a card shows it. `to` is null when no page drains the action. */
export interface WorkQueueActionView {
  key: WorkQueueAction["type"];
  title: string;
  description: string;
  linkLabel: string | null;
  to: string | null;
  jobs: WorkQueueJobView[];
  /** How many jobs the action holds beyond those listed. */
  moreJobs: number;
}

function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

function toJobView(job: WorkQueueJob, owes: (count: number) => string): WorkQueueJobView {
  return {
    id: job.jobId,
    name: job.name,
    kindLabel: BATCH_JOB_KIND_VIEWS[job.kind].label,
    status: job.status,
    owes: job.itemCount > 0 ? owes(job.itemCount) : null,
    to: ROUTES.internal.batchJobs.detail.path({ id: job.jobId }),
  };
}

/** Turns a work-queue action into the words, link and jobs of its card. */
export function describeWorkQueueAction(action: WorkQueueAction): WorkQueueActionView {
  switch (action.type) {
    case "blocked_documents":
      return {
        key: action.type,
        title: `${pluralize(action.count, "document")} to annotate`,
        description: "Blocked on a Tier-1 field the AI was not confident enough about.",
        linkLabel: "Start annotating",
        to: ROUTES.internal.annotation.path(),
        jobs: [],
        moreJobs: 0,
      };
    case "jobs_awaiting_review":
      return {
        key: action.type,
        title: `${pluralize(action.total, "batch job")} with items to review`,
        description: "Apply or dismiss each finding. A finished job still owes its reviews.",
        linkLabel: null,
        to: null,
        jobs: action.jobs.map((job) => toJobView(job, (count) => pluralize(count, "open review"))),
        moreJobs: action.total - action.jobs.length,
      };
    case "failed_jobs":
      return {
        key: action.type,
        title: `${pluralize(action.total, "batch job")} failed or with failed items`,
        description: "Retry the failed items, or archive the job once it is dealt with.",
        linkLabel: null,
        to: null,
        jobs: action.jobs.map((job) => toJobView(job, (count) => pluralize(count, "failed item"))),
        moreJobs: action.total - action.jobs.length,
      };
    case "ready_documents":
      return {
        key: action.type,
        title: `${pluralize(action.count, "document")} cleared but not embedded`,
        description:
          "These passed the confidence gate, but embedding failed. Ingesting them again finishes the job.",
        linkLabel: "Show documents",
        to: `${ROUTES.internal.library.catalogue.documents.path()}?status=ready`,
        jobs: [],
        moreJobs: 0,
      };
    case "unhealthy_cron_jobs":
      return {
        key: action.type,
        title: `${pluralize(action.jobs.length, "scheduled job")} failing`,
        description: action.jobs
          .map(
            (job) => `${job.jobName}: ${pluralize(job.consecutiveFailures, "failed run")} in a row`
          )
          .join("; "),
        linkLabel: null,
        to: null,
        jobs: [],
        moreJobs: 0,
      };
    case "escalated_taxonomy_gaps":
      return {
        key: action.type,
        title: `${pluralize(action.count, "taxonomy gap")} to decide`,
        description:
          "The judge could not settle these. Read why, and choose the entry that settles each.",
        linkLabel: "Show gaps",
        to: `${ROUTES.internal.library.taxonomy.gaps.path()}?status=escalated`,
        jobs: [],
        moreJobs: 0,
      };
  }
}
